import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { usePermissions } from '@/common/hooks/usePermissions';
import { categoriesService, Category } from '@/features/assets/services/categories.service';
import { CategoryFormDialog } from '@/features/assets/components/CategoryFormDialog';
import { ViewModeToggle, type ViewMode } from '@/components/ui/view-mode-toggle';

export const Route = createFileRoute('/_authenticated/categories')({
  component: CategoriesPage,
});

function CategoriesPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  const canEdit = hasPermission('assets.edit');

  // Buscar categorias
  const {
    data: categories,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      try {
        const response = await categoriesService.getAll();
        // response.data agora sempre será um array (garantido pelo BaseService)
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
        return [];
      }
    },
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesService.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] });
      await refetch();
    },
  });

  const handleDelete = async (category: Category) => {
    const ok = await confirm({
      title: `Excluir a categoria "${category.name}"?`,
      description:
        'Esta ação não pode ser desfeita se houver equipamentos usando esta categoria.',
      confirmLabel: 'Excluir categoria',
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(category.id);
    } catch (error) {
      // o interceptor global já mostra o toast de erro
      console.error('Erro ao excluir:', error);
    }
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedCategory(null);
    setFormOpen(true);
  };

  const handleSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['categories'] });
    await refetch();
    setFormOpen(false);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Categorias"
        description="Gerencie as categorias dos equipamentos"
        actions={
          canEdit && (
            <>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Nova categoria
              </Button>
            </>
          )
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Lista de Categorias</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : !categories || categories.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {canEdit
                ? 'Nenhuma categoria cadastrada. Clique em "Nova categoria" para começar.'
                : 'Nenhuma categoria cadastrada.'}
            </div>
          ) : viewMode === 'table' ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Realtime</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell className="font-medium">{category.name}</TableCell>
                      <TableCell>
                        {category.description || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        {category.isTrackable ? (
                          <Badge>Rastreável</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {canEdit && (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(category)}
                              title="Editar"
                              aria-label={`Editar ${category.name}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(category)}
                              title="Excluir"
                              aria-label={`Excluir ${category.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {categories.map((category) => (
                <Card key={category.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      {category.name}
                      {category.isTrackable && <Badge>Rastreável</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm text-muted-foreground">{category.description || '-'}</p>
                    {canEdit && (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(category)}>
                          Editar
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(category)}>
                          Excluir
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Formulário */}
      {canEdit && (
        <CategoryFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          onSuccess={handleSuccess}
          category={selectedCategory || undefined}
        />
      )}
    </div>
  );
}
